import { useCallback, useEffect, useRef, useState } from "react";
import { EditorialImage } from "@/components/EditorialImage";
import { cn } from "@/lib/utils";

/**
 * Galerie produit unifiée (mobile + desktop) :
 * - Une grande image principale (object-fit: cover, image ajustée au cadre sans déformation).
 * - Une rangée de miniatures juste en dessous (scroll horizontal sur mobile, wrap sur desktop).
 * - Mobile : swipe gauche/droite sur l'image principale + tap pour zoomer.
 */
export function ProductGallery({ images, alt }: { images: string[]; alt: string }) {
  const [active, setActive] = useState(0);
  const [zoomed, setZoomed] = useState(false);
  const trackRef = useRef<HTMLUListElement | null>(null);
  const thumbRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const isProgrammatic = useRef(false);

  // Réinitialise la sélection quand la couleur (donc la liste d'images) change
  const key = images.join("|");
  useEffect(() => {
    setActive(0);
    setZoomed(false);
    const el = trackRef.current;
    if (!el) return;
    isProgrammatic.current = true;
    el.scrollLeft = 0;
    const t = window.setTimeout(() => {
      isProgrammatic.current = false;
    }, 100);
    return () => window.clearTimeout(t);
  }, [key]);

  /** Suit la position du swipe pour mettre à jour l'indicateur. */
  const onScroll = useCallback(() => {
    const el = trackRef.current;
    if (!el || !el.clientWidth) return;
    const index = Math.min(
      Math.max(Math.round(el.scrollLeft / el.clientWidth), 0),
      Math.max(el.children.length - 1, 0),
    );
    setActive((prev) => (prev === index ? prev : index));
    if (!isProgrammatic.current) setZoomed(false);
  }, []);

  const goTo = (index: number, focusThumb = false) => {
    const el = trackRef.current;
    setActive(index);
    setZoomed(false);
    if (focusThumb) thumbRefs.current[index]?.focus();
    if (!el) return;
    isProgrammatic.current = true;
    el.scrollTo({ left: index * el.clientWidth, behavior: "smooth" });
    window.setTimeout(() => {
      isProgrammatic.current = false;
    }, 400);
  };

  /** Navigation clavier : flèches, Début/Fin. */
  const onKeyNav = (event: React.KeyboardEvent, focusThumb: boolean) => {
    const last = images.length - 1;
    let next: number | null = null;
    if (event.key === "ArrowRight") next = active === last ? 0 : active + 1;
    else if (event.key === "ArrowLeft") next = active === 0 ? last : active - 1;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = last;
    if (next === null) return;
    event.preventDefault();
    goTo(next, focusThumb);
  };

  return (
    <div className="min-w-0" role="group" aria-roledescription="carrousel" aria-label={`Galerie photos — ${alt}`}>
      {/* Image principale : carrousel swipe (mobile) / image sélectionnée (desktop) */}
      <div className="relative">
        <ul
          ref={trackRef}
          onScroll={onScroll}
          onKeyDown={(e) => onKeyNav(e, false)}
          aria-label={`Galerie ${alt}`}
          className="flex snap-x snap-mandatory overflow-x-auto overscroll-x-contain scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {images.map((src, i) => (
            <li
              key={src}
              id={`galerie-visuel-${i}`}
              role="group"
              aria-roledescription="diapositive"
              aria-label={`Visuel ${i + 1} sur ${images.length}`}
              aria-hidden={i !== active}
              className="w-full min-w-full shrink-0 grow-0 basis-full snap-center"
            >
              <button
                type="button"
                tabIndex={i === active ? 0 : -1}
                onClick={() => setZoomed((z) => (active === i ? !z : false))}
                aria-pressed={zoomed && active === i}
                aria-label={
                  zoomed && active === i
                    ? `Réduire le visuel ${i + 1}`
                    : `Agrandir le visuel ${i + 1} sur ${images.length}. Utilisez les flèches gauche et droite pour changer d'image.`
                }
                className="block w-full overflow-hidden border border-border bg-sand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                <div
                  className={cn(
                    "transition-transform duration-700 ease-out will-change-transform",
                    zoomed && active === i ? "scale-[1.6]" : "scale-100",
                  )}
                >
                  <EditorialImage
                    src={src}
                    alt={`${alt} — visuel ${i + 1}`}
                    ratio="product"
                    fit="cover"
                    className="!bg-sand"
                    imageClassName="!transform-none"
                    priority={i === 0}
                    sizes="(min-width: 1024px) 45vw, 100vw"
                  />
                </div>
              </button>
            </li>
          ))}
        </ul>

        {/* Compteur discret */}
        {images.length > 1 ? (
          <div className="pointer-events-none absolute bottom-3 right-3 rounded-full bg-background/85 px-3 py-1 backdrop-blur-sm">
            <span aria-hidden="true" className="label-xs text-[0.6rem] tabular-nums">
              {active + 1} / {images.length}
            </span>
          </div>
        ) : null}
      </div>

      {/* Annonce lecteur d'écran de l'image affichée */}
      <p aria-live="polite" className="sr-only">
        {images.length > 1 ? `Visuel ${active + 1} sur ${images.length}` : ""}
      </p>

      {/* Miniatures : dimensions fixes pour éviter tout décalage */}
      {images.length > 1 ? (
        <ul
          role="tablist"
          aria-label={`Miniatures — ${alt}`}
          onKeyDown={(e) => onKeyNav(e, true)}
          className="mt-3 flex flex-nowrap gap-3 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] sm:flex-wrap sm:overflow-visible [&::-webkit-scrollbar]:hidden"
        >
          {images.map((src, i) => (
            <li key={src} role="presentation" className="w-[72px] shrink-0 grow-0 sm:w-[84px]">
              <button
                type="button"
                role="tab"
                ref={(node) => {
                  thumbRefs.current[i] = node;
                }}
                onClick={() => goTo(i)}
                aria-label={`Afficher le visuel ${i + 1} sur ${images.length}`}
                aria-selected={i === active}
                aria-controls={`galerie-visuel-${i}`}
                tabIndex={i === active ? 0 : -1}
                className={cn(
                  "block w-full overflow-hidden border bg-sand transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2",
                  i === active ? "border-foreground" : "border-border hover:border-foreground",
                )}
              >
                <EditorialImage
                  src={src}
                  alt=""
                  ratio="product"
                  fit="cover"
                  className="!bg-sand"
                  imageClassName="!transform-none"
                  sizes="96px"
                />
              </button>
            </li>
          ))}
        </ul>
      ) : null}


      <p className="label-xs mt-3 text-center text-[0.55rem] text-muted-foreground sm:hidden">
        Glissez pour parcourir · Touchez l’image pour zoomer
      </p>
    </div>
  );

}
